// src/services/printer/printQueueService.js
const QUEUE_STORAGE_KEY = "printerQueue";
const MAX_RETRIES = 3;

class PrintQueueService {
  constructor() {
    this.queue = new Map();
    this.loadQueue();
  }

  // Cargar la cola desde localStorage
  loadQueue() {
    try {
      const savedQueue = localStorage.getItem(QUEUE_STORAGE_KEY);
      if (savedQueue) {
        const parsedQueue = JSON.parse(savedQueue);
        this.queue = new Map(parsedQueue);
      }
    } catch (error) {
      console.error("Error loading print queue:", error);
      this.queue = new Map();
    }
  }

  // Guardar la cola en localStorage
  saveQueue() {
    try {
      const queueArray = Array.from(this.queue.entries());
      localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(queueArray));
    } catch (error) {
      console.error("Error saving print queue:", error);
    }
  }

  // Agregar un trabajo de impresión a la cola
  addToQueue(printJob) {
    const jobId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const job = {
      ...printJob,
      id: jobId,
      status: "pending",
      retries: 0,
      timestamp: Date.now(),
      lastAttempt: null,
    };

    this.queue.set(jobId, job);
    this.saveQueue();
    return jobId;
  }

  // Marcar un trabajo como completado y removerlo de la cola
  markAsCompleted(jobId) {
    this.queue.delete(jobId);
    this.saveQueue();
  }

  // Marcar un trabajo como fallido
  markAsFailed(jobId, error) {
    const job = this.queue.get(jobId);
    if (job) {
      job.status = "failed";
      job.lastError = error.message;
      job.lastAttempt = Date.now();
      job.retries += 1;

      if (job.retries >= MAX_RETRIES) {
        job.status = "abandoned";
      }

      this.queue.set(jobId, job);
      this.saveQueue();
    }
  }

  // Obtener todos los trabajos pendientes
  getPendingJobs() {
    return Array.from(this.queue.values()).filter(
      (job) =>
        job.status === "pending" ||
        (job.status === "failed" && job.retries < MAX_RETRIES)
    );
  }

  // Obtener trabajos pendientes para una impresora específica
  getPendingJobsForPrinter(printerId) {
    return this.getPendingJobs().filter((job) => job.printerId === printerId);
  }

  // Limpiar trabajos antiguos o abandonados
  cleanupOldJobs(maxAge = 7 * 24 * 60 * 60 * 1000) {
    // 7 días por defecto
    const now = Date.now();
    for (const [jobId, job] of this.queue.entries()) {
      if (job.status === "abandoned" || now - job.timestamp > maxAge) {
        this.queue.delete(jobId);
      }
    }
    this.saveQueue();
  }

  // Reiniciar trabajos fallidos
  resetFailedJobs() {
    for (const [jobId, job] of this.queue.entries()) {
      if (job.status === "failed" && job.retries < MAX_RETRIES) {
        job.status = "pending";
        this.queue.set(jobId, job);
      }
    }
    this.saveQueue();
  }
}

export const printQueueService = new PrintQueueService();
